"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, BarChart3, Building2, Gauge, LayoutDashboard, LogOut, Menu, MessageCircle, MessageSquare, Plug } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupLabel, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { useAuth } from "@/lib/auth-client";
import { Logo } from "./ui/logo";
import type { ReactNode } from "react";
interface AdminShellProps {
  children: ReactNode;
}
function MobileHeader() {
  const $ = _c(8);
  const {
    toggleSidebar
  } = useSidebar();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Menu className="h-5 w-5" />;
    t1 = <span className="sr-only">Toggle menu</span>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== toggleSidebar) {
    t2 = <Button variant="ghost" size="icon" className="h-9 w-9" onClick={toggleSidebar}>{t0}{t1}</Button>;
    $[2] = toggleSidebar;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary"><Logo className="h-4 w-4" /></div>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center gap-2">{t3}<div className="flex flex-col"><span className="text-sm font-semibold leading-tight">LLM Gateway</span><span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Admin</span></div></div>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2) {
    t5 = <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-border/60 bg-background px-4 md:hidden">{t2}{t4}</header>;
    $[6] = t2;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
export function AdminShell(t0) {
  const $ = _c(71);
  const {
    children
  } = t0;
  const pathname = usePathname();
  const router = useRouter();
  const {
    signOut
  } = useAuth();
  const queryClient = useQueryClient();
  const isDashboard = pathname === "/" || pathname === "";
  let t1;
  if ($[0] !== pathname) {
    t1 = pathname.startsWith("/organizations");
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isOrganizations = t1;
  let t2;
  if ($[2] !== pathname) {
    t2 = pathname.startsWith("/chat-plans");
    $[2] = pathname;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isChatPlans = t2;
  let t3;
  if ($[4] !== pathname) {
    t3 = pathname.startsWith("/global-stats");
    $[4] = pathname;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const isGlobalStats = t3;
  const isRateLimits = pathname === "/rate-limits";
  const isPlatformModels = pathname === "/platform-models";
  let t4;
  if ($[6] !== pathname) {
    t4 = pathname.startsWith("/chat-support-logs");
    $[6] = pathname;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const isChatSupportLogs = t4;
  let t5;
  if ($[8] !== pathname) {
    t5 = pathname.startsWith("/payment-failures");
    $[8] = pathname;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const isPaymentFailures = t5;
  let t6;
  if ($[10] !== queryClient || $[11] !== router || $[12] !== signOut) {
    t6 = async () => {
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push("/login");
          }
        }
      });
    };
    $[10] = queryClient;
    $[11] = router;
    $[12] = signOut;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleSignOut = t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary"><Logo className="h-4 w-4" /></div>;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <SidebarHeader className="border-b border-sidebar-border/60"><div className="flex h-12 items-center justify-between px-2"><div className="flex items-center gap-2 px-1">{t7}<div className="flex flex-col"><span className="text-sm font-semibold leading-tight">LLM Gateway</span><span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Admin</span></div></div><SidebarTrigger className="hidden md:flex" /></div></SidebarHeader>;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SidebarGroupLabel>Main</SidebarGroupLabel>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <LayoutDashboard className="h-4 w-4" />;
    t11 = <span>Dashboard</span>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t10 = $[17];
    t11 = $[18];
  }
  let t12;
  if ($[19] !== isDashboard) {
    t12 = <SidebarMenuItem><Link href="/" className="block"><SidebarMenuButton isActive={isDashboard} size="lg">{t10}{t11}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[19] = isDashboard;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Building2 className="h-4 w-4" />;
    t14 = <span>Organizations</span>;
    $[21] = t13;
    $[22] = t14;
  } else {
    t13 = $[21];
    t14 = $[22];
  }
  let t15;
  if ($[23] !== isOrganizations) {
    t15 = <SidebarMenuItem><Link href="/organizations" className="block"><SidebarMenuButton isActive={isOrganizations} size="lg">{t13}{t14}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[23] = isOrganizations;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <MessageSquare className="h-4 w-4" />;
    t17 = <span>Chat Plans</span>;
    $[25] = t16;
    $[26] = t17;
  } else {
    t16 = $[25];
    t17 = $[26];
  }
  let t18;
  if ($[27] !== isChatPlans) {
    t18 = <SidebarMenuItem><Link href="/chat-plans" className="block"><SidebarMenuButton isActive={isChatPlans} size="lg">{t16}{t17}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[27] = isChatPlans;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  let t20;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <BarChart3 className="h-4 w-4" />;
    t20 = <span>Global Stats</span>;
    $[29] = t19;
    $[30] = t20;
  } else {
    t19 = $[29];
    t20 = $[30];
  }
  let t21;
  if ($[31] !== isGlobalStats) {
    t21 = <SidebarMenuItem><Link href="/global-stats" className="block"><SidebarMenuButton isActive={isGlobalStats} size="lg">{t19}{t20}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[31] = isGlobalStats;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  let t23;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Gauge className="h-4 w-4" />;
    t23 = <span>Global Rate Limits</span>;
    $[33] = t22;
    $[34] = t23;
  } else {
    t22 = $[33];
    t23 = $[34];
  }
  let t24;
  if ($[35] !== isRateLimits) {
    t24 = <SidebarMenuItem><Link href="/rate-limits" className="block"><SidebarMenuButton isActive={isRateLimits} size="lg">{t22}{t23}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[35] = isRateLimits;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  let t26;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Plug className="h-4 w-4" />;
    t26 = <span>Platform Models</span>;
    $[37] = t25;
    $[38] = t26;
  } else {
    t25 = $[37];
    t26 = $[38];
  }
  let t27;
  if ($[39] !== isPlatformModels) {
    t27 = <SidebarMenuItem><Link href="/platform-models" className="block"><SidebarMenuButton isActive={isPlatformModels} size="lg">{t25}{t26}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[39] = isPlatformModels;
    $[40] = t27;
  } else {
    t27 = $[40];
  }
  let t28;
  let t29;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <MessageCircle className="h-4 w-4" />;
    t29 = <span>Chat Support Logs</span>;
    $[41] = t28;
    $[42] = t29;
  } else {
    t28 = $[41];
    t29 = $[42];
  }
  let t30;
  if ($[43] !== isChatSupportLogs) {
    t30 = <SidebarMenuItem><Link href="/chat-support-logs" className="block"><SidebarMenuButton isActive={isChatSupportLogs} size="lg">{t28}{t29}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[43] = isChatSupportLogs;
    $[44] = t30;
  } else {
    t30 = $[44];
  }
  let t31;
  let t32;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <AlertTriangle className="h-4 w-4" />;
    t32 = <span>Payment Failures</span>;
    $[45] = t31;
    $[46] = t32;
  } else {
    t31 = $[45];
    t32 = $[46];
  }
  let t33;
  if ($[47] !== isPaymentFailures) {
    t33 = <SidebarMenuItem><Link href="/payment-failures" className="block"><SidebarMenuButton isActive={isPaymentFailures} size="lg">{t31}{t32}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[47] = isPaymentFailures;
    $[48] = t33;
  } else {
    t33 = $[48];
  }
  let t34;
  if ($[49] !== t12 || $[50] !== t15 || $[51] !== t18 || $[52] !== t21 || $[53] !== t24 || $[54] !== t27 || $[55] !== t30 || $[56] !== t33) {
    t34 = <SidebarContent><SidebarGroup>{t9}<SidebarMenu>{t12}{t15}{t18}{t21}{t24}{t27}{t30}{t33}</SidebarMenu></SidebarGroup></SidebarContent>;
    $[49] = t12;
    $[50] = t15;
    $[51] = t18;
    $[52] = t21;
    $[53] = t24;
    $[54] = t27;
    $[55] = t30;
    $[56] = t33;
    $[57] = t34;
  } else {
    t34 = $[57];
  }
  let t35;
  let t36;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <LogOut className="h-3.5 w-3.5" />;
    t36 = <span>Sign out</span>;
    $[58] = t35;
    $[59] = t36;
  } else {
    t35 = $[58];
    t36 = $[59];
  }
  let t37;
  if ($[60] !== handleSignOut) {
    t37 = <SidebarFooter className="border-t border-sidebar-border/60"><Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-xs text-muted-foreground" onClick={handleSignOut}>{t35}{t36}</Button></SidebarFooter>;
    $[60] = handleSignOut;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  let t38;
  if ($[62] !== t34 || $[63] !== t37) {
    t38 = <Sidebar variant="inset">{t8}{t34}{t37}</Sidebar>;
    $[62] = t34;
    $[63] = t37;
    $[64] = t38;
  } else {
    t38 = $[64];
  }
  let t39;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <MobileHeader />;
    $[65] = t39;
  } else {
    t39 = $[65];
  }
  let t40;
  if ($[66] !== children) {
    t40 = <SidebarInset>{t39}{children}</SidebarInset>;
    $[66] = children;
    $[67] = t40;
  } else {
    t40 = $[67];
  }
  let t41;
  if ($[68] !== t38 || $[69] !== t40) {
    t41 = <SidebarProvider>{t38}{t40}</SidebarProvider>;
    $[68] = t38;
    $[69] = t40;
    $[70] = t41;
  } else {
    t41 = $[70];
  }
  return t41;
}